/**
 * 이상한 방 - 와이어 모양 검사 (플레이어 이벤트 본문 · 셀 · 오브젝트 · 설정 패치)
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 11.3절(악의적 클라이언트 한 줄로 호스트가 던지면 안 된다) · `protocol.ts` 머리말("형태 검증은 얕다")
 *
 * `protocol.ts`에서 갈라 나왔다(코덱 + 모양표가 한 파일에서 400줄을 넘었다). 여기 있는 것은 전부 **모양**이다 - 필드가 그 타입으로
 * 있는가. 값의 뜻(셀이 방 안인가 · 걸기 개수가 예산 안인가 · 그 물건이 있는가)은 리듀서가 `core/verbs`·`raise.normalizeWager`·`hunt.resolveShot`으로
 * 검증한다. 두 층이 같은 검사를 갖지 않게 하는 선이 그것이다.
 *
 * `PLAYER_EVENT_SHAPES`는 `PlayerEventType`(원본 `matchTypes.PLAYER_EVENT_TYPES`)을 키로 하는 `Record`라 이벤트가 하나 늘면 여기 한 줄이
 * 없으면 컴파일이 멈춘다 - "모양 검사를 잊은 새 이벤트"가 화면에서 `malformed`로 드러나기 전에 잡힌다.
 *
 * `net/`은 `core`·`game/match`·`game` 리프만 본다(`boundary.test.ts`). 서버가 그대로 import 한다.
 */

import { VERBS } from '../core/types'
import type { MatchConfig, PlayerEventType } from '../game/match/matchTypes'

export type JsonObject = Record<string, unknown>

export function isObject(value: unknown): value is JsonObject {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** 셀의 모양 - 셋이 숫자. 값의 범위(방 안인가)는 리듀서(verbs) · 팬아웃하는 쪽(M8)이 본다. */
function isCell(value: unknown): boolean {
  return isObject(value) && typeof value['x'] === 'number' && typeof value['y'] === 'number' && typeof value['z'] === 'number'
}

/** `presence.aim`의 모양 - 셀이거나 `null`. */
export function isCellOrNull(value: unknown): boolean {
  return value === null || isCell(value)
}

const VERB_SET: ReadonlySet<unknown> = new Set(VERBS)

/** 와이어 위의 오브젝트(`roomCodec.WireObject`와 같은 다섯 필드). `placedBy`가 있어도 무시한다 - 호스트가 신원으로 덮어쓴다. */
function isWireObjectShape(value: unknown): boolean {
  return (
    isObject(value) &&
    typeof value['id'] === 'string' &&
    typeof value['catalogId'] === 'string' &&
    isCell(value['cell']) &&
    typeof value['rot'] === 'number' &&
    typeof value['colorId'] === 'number'
  )
}

/**
 * `applyChange.request`의 모양(머리말 "값이 아니라 모양만"). 동사는 정본 `VERBS` 중 하나여야 하고, 리듀서가 읽는 필드가
 * 그 타입으로 있어야 한다.
 */
function isVerbRequestShape(value: unknown): boolean {
  if (!isObject(value) || !VERB_SET.has(value['verb'])) return false
  if (value['verb'] === 'add') return isWireObjectShape(value['obj'])
  if (typeof value['id'] !== 'string') return false
  if (value['verb'] === 'move') return isCell(value['cell'])
  if (value['verb'] === 'rotate') return typeof value['rot'] === 'number'
  if (value['verb'] === 'recolor') return typeof value['colorId'] === 'number'
  return true
}

const HIT_KINDS: ReadonlySet<unknown> = new Set<string>(['object', 'proxy'])

/** `shoot.hit`의 모양 - `null`(빗나감)이거나 `hunt.HitTarget`의 두 필드. 그 물건이 있는가는 판정기(`resolveShot`)의 몫이다. */
function isHitOrNull(value: unknown): boolean {
  if (value === null) return true
  return isObject(value) && HIT_KINDS.has(value['kind']) && typeof value['instanceId'] === 'string'
}

/**
 * `commitChanges.wager`의 모양 - `null`이거나 `{ unfoundAtLeast: 정수 }`. 개수가 예산 안인가는 리듀서(`raise.normalizeWager`)가
 * 접는다 - 거기서 `undefined`·문자열도 접는 것은 **값** 방어이고(7절 M7 기록), 와이어 모양 방어는 여기다(머리말).
 */
function isWagerOrNull(value: unknown): boolean {
  if (value === null) return true
  return isObject(value) && Number.isInteger(value['unfoundAtLeast'])
}

/** `configure.patch.room`의 모양 - 있는 필드만 타입을 본다(부분 패치). `allowedVerbs`는 와이어에서 배열이고 원소가 동사다. */
function isRoomPatchShape(value: unknown): boolean {
  if (!isObject(value)) return false
  if ('allowedVerbs' in value && !(Array.isArray(value['allowedVerbs']) && value['allowedVerbs'].every((verb) => VERB_SET.has(verb)))) {
    return false
  }
  if ('changeCount' in value && !Number.isInteger(value['changeCount'])) return false
  if ('placementsPerPlayer' in value && !Number.isInteger(value['placementsPerPlayer'])) return false
  return true
}

/**
 * `configure.patch`의 모양. 리듀서 `onConfigure`가 `{ ...config, ...patch }`로 **통째로 펴므로** 모르는 키가 그대로 설정에 박힌다 -
 * 그래서 아는 키만 허용하고 각각 타입을 본다. 설정에 필드가 늘면 `MatchConfig`(원본)와 함께 이 표도 늘어야 하고, 잊으면
 * 방장의 새 설정이 `malformed`로 튕겨 바로 드러난다(조용히 통과하는 쪽보다 낫다).
 */
function isConfigPatchShape(value: unknown): boolean {
  if (!isObject(value)) return false
  for (const [key, field] of Object.entries(value)) {
    switch (key) {
      case 'room':
        if (!isRoomPatchShape(field)) return false
        break
      case 'revealVerbCounts':
      case 'humanDecorate':
        if (typeof field !== 'boolean') return false
        break
      default:
        return false
    }
  }
  return true
}

/**
 * 이벤트 타입마다 본문 모양 검사 하나. 필드가 없는 이벤트는 `true`다(판별자만 있으면 리듀서가 읽을 것이 없다).
 * `PLAYER_EVENT_TYPES`(원본 `matchTypes`)와 키가 같아야 한다 - 빠지면 컴파일이 멈춘다(`Record`).
 */
const PLAYER_EVENT_SHAPES: Readonly<Record<PlayerEventType, (event: JsonObject) => boolean>> = {
  ready: (event) => typeof event['ready'] === 'boolean',
  configure: (event) => isConfigPatchShape(event['patch']),
  start: () => true,
  nextRound: () => true,
  skipPhase: () => true,
  place: (event) => isWireObjectShape(event['obj']),
  unplace: (event) => typeof event['id'] === 'string',
  applyChange: (event) => isVerbRequestShape(event['request']),
  undoChange: (event) => typeof event['id'] === 'string',
  commitChanges: (event) => isWagerOrNull(event['wager']),
  shoot: (event) => isHitOrNull(event['hit']),
  useMagazine: () => true,
  raise: () => true,
  requestOrigin: () => true,
  leaveOrigin: () => true,
  leaveQueue: () => true,
  spectate: (event) => event['target'] === null || typeof event['target'] === 'string',
  predict: (event) => isCell(event['cell']),
  voteUnfair: (event) => typeof event['changeId'] === 'string' && typeof event['unfair'] === 'boolean',
}

/** 플레이어 이벤트 본문의 모양. 타입은 이미 `isPlayerEventType`을 지났다. */
export function isPlayerEventShape(type: PlayerEventType, event: JsonObject): boolean {
  return PLAYER_EVENT_SHAPES[type](event)
}
